import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Package,
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter,
  ChevronRight,
  ArrowLeft,
  Store,
  DollarSign,
  Tag,
  Image as ImageIcon,
  Loader,
  TrendingUp,
  Star
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import marketplaceAPI from '../../services/marketplace.api';
import useMarketplaceStore from '../../store/marketplaceStore';
import ProductModal from '../../components/community/ProductModal';
import Swal from 'sweetalert2';

const ProductManagementPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { selectedBusiness, getBusinessDetails } = useMarketplaceStore();

  useEffect(() => {
    loadData();
  }, [businessId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await getBusinessDetails(businessId);
      await loadProducts();
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await marketplaceAPI.getProducts(businessId);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    const result = await Swal.fire({
      title: 'Delete Product',
      text: 'Are you sure you want to delete this product? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      customClass: {
        popup: 'rounded-xl shadow-neomorph',
        title: 'text-gray-800',
        htmlContainer: 'text-gray-600'
      }
    });
    
    if (!result.isConfirmed) return;
    
    try {
      await marketplaceAPI.deleteProduct(productId);
      toast.success('Product deleted successfully');
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to delete product');
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Stats for header
  const stats = {
    total: products.length,
    inStock: products.filter(p => p.quantity > 0).length,
    featured: products.filter(p => p.featured).length,
    totalValue: products.reduce((sum, p) => sum + (p.price * p.quantity), 0)
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5] pb-16 sm:pb-0">
      {/* Header */}
      <div className="bg-[#E8ECF5] border-b border-primary-100/50 shadow-neomorph sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-5">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div className="flex items-center min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="mr-2 sm:mr-3 md:mr-4 p-1.5 sm:p-2 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
              </button>
              <div className="flex items-center min-w-0">
                <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 mr-2 sm:mr-4 flex-shrink-0">
                  <Package className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center mb-0.5 sm:mb-1">
                    <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                    <h1 className="text-sm sm:text-base md:text-xl font-bold text-gray-800 truncate">Manage Products</h1>
                  </div>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 ml-2 sm:ml-3 truncate">
                    {selectedBusiness?.name} • {products.length} products
                  </p>
                </div>
              </div>
            </div>

            {/* Add Product Button - Mobile optimized */}
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-xs sm:text-sm font-medium flex items-center justify-center"
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Add Product</span>
              <span className="xs:hidden">Add</span>
            </button>
          </div>

          {/* Stats Badges - Row on mobile, horizontal scroll if needed */}
          <div className="flex flex-wrap gap-2 mt-3 sm:mt-4 pt-2 border-t border-primary-100/50">
            <div className="bg-white rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 shadow-neomorph-inset">
              <p className="text-[8px] sm:text-xs text-gray-500">In Stock</p>
              <p className="text-[10px] sm:text-sm font-semibold text-green-600">{stats.inStock}</p>
            </div>
            <div className="bg-white rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 shadow-neomorph-inset">
              <p className="text-[8px] sm:text-xs text-gray-500">Featured</p>
              <p className="text-[10px] sm:text-sm font-semibold text-yellow-600">{stats.featured}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - 2x2 grid on mobile */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-5">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          <div className="bg-white rounded-lg p-2.5 sm:p-4 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">Total Products</p>
                <p className="text-sm sm:text-base md:text-lg font-bold text-gray-800">{stats.total}</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-md shadow-md">
                <Package className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-2.5 sm:p-4 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">In Stock</p>
                <p className="text-sm sm:text-base md:text-lg font-bold text-green-600">{stats.inStock}</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-md shadow-md">
                <Tag className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-2.5 sm:p-4 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">Featured</p>
                <p className="text-sm sm:text-base md:text-lg font-bold text-yellow-600">{stats.featured}</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-md shadow-md">
                <Star className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-2.5 sm:p-4 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">Inventory Value</p>
                <p className="text-sm sm:text-base md:text-lg font-bold text-purple-600 truncate max-w-[80px] sm:max-w-none">
                  {formatAmount(stats.totalValue).slice(0, 12)}...
                </p>
              </div>
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow-md">
                <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pb-4 sm:pb-6 md:pb-8">
        {/* Search Bar */}
        <div className="bg-white rounded-xl p-3 sm:p-4 shadow-neomorph border border-gray-100 mb-4 sm:mb-5">
          <div className="relative">
            <Search className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="block w-full pl-8 sm:pl-9 pr-2.5 sm:pr-3 py-1.5 sm:py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
            />
          </div>
        </div>

        {/* Products Grid - 2 columns on mobile, 3 on tablet, 4 on desktop */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white rounded-xl p-3 sm:p-4 shadow-neomorph border border-gray-100 animate-pulse">
                <div className="h-28 sm:h-48 bg-gray-200 rounded-lg mb-2 sm:mb-4 shadow-neomorph-inset"></div>
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4 mb-1.5 sm:mb-2"></div>
                <div className="h-2 sm:h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
            {filteredProducts.map(product => (
              <div key={product.id} className="bg-white rounded-xl p-3 sm:p-4 shadow-neomorph border border-gray-100 hover:shadow-neomorph-hover transition-all">
                {/* Product Image */}
                {product.images?.[0] ? (
                  <div className="w-full h-28 sm:h-48 bg-[#E8ECF5] rounded-lg mb-2 sm:mb-4 shadow-neomorph-inset overflow-hidden">
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-full h-28 sm:h-48 bg-[#E8ECF5] rounded-lg mb-2 sm:mb-4 shadow-neomorph-inset flex items-center justify-center">
                    <Package className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                  </div>
                )}
                
                <div className="space-y-1.5 sm:space-y-3">
                  {/* Title and Featured Badge */}
                  <div className="flex items-start justify-between gap-1">
                    <h3 className="font-semibold text-gray-800 text-xs sm:text-sm line-clamp-2 flex-1">{product.name}</h3>
                    {product.featured && (
                      <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-yellow-50 text-yellow-700 text-[8px] sm:text-xs font-medium rounded-lg border border-yellow-200 shadow-neomorph-inset whitespace-nowrap">
                        Featured
                      </span>
                    )}
                  </div>
                  
                  {/* Description - Hidden on mobile */}
                  <p className="hidden sm:block text-xs text-gray-500 line-clamp-2 bg-[#E8ECF5] p-2 rounded-lg shadow-neomorph-inset">
                    {product.description}
                  </p>
                  
                  {/* Price and Stock */}
                  <div className="flex flex-wrap items-center justify-between gap-1">
                    <div className="flex items-baseline flex-wrap">
                      <span className="text-sm sm:text-lg font-bold text-primary-600">
                        {formatAmount(product.price).slice(0, -2)}
                      </span>
                      {product.compareAtPrice && (
                        <span className="ml-1 sm:ml-2 text-[8px] sm:text-xs text-gray-400 line-through">
                          {formatAmount(product.compareAtPrice).slice(0, -2)}
                        </span>
                      )}
                    </div>
                    <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[7px] sm:text-xs font-medium rounded-lg ${
                      product.quantity > 0 
                        ? 'bg-green-50 text-green-700 border border-green-200 shadow-neomorph-inset'
                        : 'bg-red-50 text-red-700 border border-red-200 shadow-neomorph-inset'
                    }`}>
                      {product.quantity > 0 ? `${product.quantity}` : 'Out'}
                    </span>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between pt-1.5 sm:pt-3 border-t border-gray-100">
                    <div className="flex space-x-0.5 sm:space-x-1">
                      <button
                        onClick={() => {/* View product details */}}
                        className="p-1.5 sm:p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-600"
                        title="View"
                      >
                        <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowAddModal(true);
                        }}
                        className="p-1.5 sm:p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-blue-600"
                        title="Edit"
                      >
                        <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="p-1.5 sm:p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                    <span className="text-[7px] sm:text-xs text-gray-500 bg-[#E8ECF5] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg shadow-neomorph-inset truncate max-w-[60px] sm:max-w-none">
                      SKU: {product.sku?.slice(0, 6) || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl p-6 sm:p-8 shadow-neomorph border border-gray-100 text-center">
            <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Package className="h-7 w-7 sm:h-10 sm:w-10 text-primary-600" />
            </div>
            <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">No products found</h3>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
              {searchQuery ? 'Try a different search' : 'Start adding products to your business'}
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 sm:px-6 py-1.5 sm:py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-xs sm:text-sm font-medium inline-flex items-center"
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Add First Product
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Product Modal */}
      <ProductModal
        isOpen={showAddModal}
        businessId={businessId}
        product={selectedProduct}
        onClose={() => {
          setShowAddModal(false);
          setSelectedProduct(null);
        }}
        onSuccess={() => {
          loadProducts();
          setShowAddModal(false);
          setSelectedProduct(null);
        }}
      />

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default ProductManagementPage;